import { COMPANY } from "./company";
import { RESOURCE_ARTICLES } from "../resources/catalog";

/**
 * The public routes, and the metadata each one needs in its own HTML document.
 *
 * Google's OAuth review fetches these pages without executing JavaScript. Every one of
 * them is therefore pre-rendered to static HTML at build time, with its own title,
 * description and canonical — see `scripts/prerender.mjs`.
 */
export type PublicRoute = {
  path: string;
  /** Output file under dist/. */
  file: string;
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: "website" | "article";
};

export const PUBLIC_ROUTES: PublicRoute[] = [
  {
    path: "/resources",
    file: "resources/index.html",
    title: `Social media resources and comparisons | ${COMPANY.product}`,
    description: "Explore social scheduling guides, 11 competitor comparisons, and Simino Social’s AI product direction with human control. Find the workflow that fits your business.",
  },
  ...RESOURCE_ARTICLES.map(article => ({
    path: `/resources/${article.slug}`,
    file: `resources/${article.slug}/index.html`,
    title: `${article.title} | ${COMPANY.product}`,
    description: article.description,
    ogType: "article" as const,
  })),
  {
    path: "/resources/why-scheduling",
    ogType: "article",
    file: "resources/why-scheduling/index.html",
    title: `Why social media scheduling matters | ${COMPANY.product}`,
    description: "Your customers are scrolling, even when you’re busy. Discover how scheduling helps you post consistently, avoid last-minute mistakes, and keep your brand visible.",
  },
  {
    path: "/",
    file: "index.html",
    title: "Simino Social | AI-Assisted Social Media Management",
    description: "Plan, create, schedule and publish social media content from one place. AI-assisted, human-led social media management for businesses and creators.",
  },
  {
    path: "/social-media-scheduler",
    file: "social-media-scheduler/index.html",
    title: "Social Media Scheduler for Small Businesses & Creators | Simino Social",
    description: "Plan and schedule social media posts from one place. Simino Social helps businesses and creators stay consistent with AI-assisted planning and human control.",
  },
  {
    path: "/app",
    file: "app/index.html",
    title: `Simino Social | AI Social Media Management & Scheduling`,
    description:
      `Create, schedule, publish and track social media content across Facebook, Instagram ` +
      `and YouTube with Simino Social's streamlined publishing workflow.`,
    ogTitle: "Simino Social — AI-Assisted. Human-Led.",
    ogDescription:
      "Create, schedule, publish and track social content from one workspace. Social media management built for SMBs, creators and influencers.",
    ogImage: "/app-og.png",
  },
  {
    path: "/contact",
    file: "contact/index.html",
    title: `Contact ${COMPANY.product} — tell us what you need made`,
    description:
      "Get in touch about Simino Social, our creative partners, or a piece of content " +
      "you need produced. No account needed: confirm your address from the link we send " +
      "and a person replies. Monitored mailbox and published phone number too.",
  },
  {
    path: "/partners",
    file: "partners/index.html",
    title: `Partners | ${COMPANY.product}`,
    description:
      "Simino Social publishes your content; our partners make it. Why the craft is worth " +
      "sharing, who we introduce you to, and how one quote covers both the production and " +
      "your plan.",
    ogTitle: "The tool is ours. The craft is worth sharing.",
  },
  {
    path: "/partners/content-creator",
    file: "partners/content-creator/index.html",
    title: `Why your brand needs a human content creator | ${COMPANY.product}`,
    description:
      "AI can generate the image in seconds, but it cannot hold your brand system, " +
      "judge what is worth publishing or own the sign-off. Why a specialist content " +
      "creator is what makes a brand shine.",
    ogTitle: "AI can make the image. A human makes it mean something.",
  },
  {
    path: "/partners/graphaize",
    file: "partners/graphaize/index.html",
    title: `Graphaize — premium visual content and explainer video partner | ${COMPANY.product}`,
    description:
      "A profile of Graphaize, our creative partner: explainer and educational " +
      "video, infographics, pitch decks, ebooks, microsites and social ads, with their " +
      "concept-visualize-animate process and how to reach them.",
    ogTitle: "Graphaize — premium visual content and explainer video",
  },
  {
    path: "/how-it-works",
    file: "how-it-works/index.html",
    title: `How ${COMPANY.product} works — Connect, compose, schedule and publish`,
    description:
      `See how ${COMPANY.product} connects Facebook Pages, Instagram Business accounts and ` +
      `YouTube channels, checks media, schedules posts and reports each destination separately.`,
  },
  {
    path: "/pricing",
    file: "pricing/index.html",
    title: `Pricing | ${COMPANY.product}`,
    description:
      `${COMPANY.product} pricing for India, the United States and Canada, and the rest of the ` +
      `world — monthly, yearly or a one-off lifetime plan. Start free and upgrade when you need ` +
      `video publishing, scheduling or more connected accounts.`,
  },
  {
    path: "/security",
    file: "security/index.html",
    title: `Security and trust | ${COMPANY.product}`,
    description:
      `How ${COMPANY.product} protects your connected accounts: we never receive your ` +
      `password, tokens are encrypted, and you can revoke access at any time.`,
  },
  {
    path: "/about",
    file: "about/index.html",
    title: `About us | ${COMPANY.product}`,
    description:
      `${COMPANY.product} is a product of ${COMPANY.legalName}, a registered Indian company. ` +
      `Who operates the product, the legal entity behind it, and how to reach a person.`,
  },
  {
    path: "/support",
    file: "support/index.html",
    title: `Support | ${COMPANY.product}`,
    description:
      `Get help with ${COMPANY.product} sign-in, connected accounts, media uploads, scheduling ` +
      `and publishing, or contact ${COMPANY.legalName} support.`,
  },
  {
    path: "/privacy",
    file: "privacy/index.html",
    title: `Privacy Policy | ${COMPANY.product}`,
    description:
      `How ${COMPANY.product} collects, uses, stores and deletes personal information, ` +
      `including data received from the Meta and YouTube APIs.`,
  },
  {
    path: "/terms",
    file: "terms/index.html",
    title: `Terms of Service | ${COMPANY.product}`,
    description: `The terms governing your use of ${COMPANY.product}, operated by ${COMPANY.legalName}.`,
  },
  {
    path: "/data-deletion",
    file: "data-deletion/index.html",
    title: `Delete your data | ${COMPANY.product}`,
    description:
      `Step-by-step instructions to delete your ${COMPANY.product} account and data, and to ` +
      `revoke access to your Facebook, Instagram and YouTube accounts.`,
  },
];

/** Paths whose served HTML already contains the rendered page, so the client hydrates. */
export const PRERENDERED_PATHS = new Set(PUBLIC_ROUTES.map((r) => r.path));
